import Avatar from 'components/Avatar';
import DynamicUserAvatar from 'components/DynamicUserAvatar';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import {
  AvatarAnomaly,
  AvatarArchived,
  AvatarCreate,
  AvatarDeleted,
  AvatarEdit,
} from 'components/Icons';
import ProgressBar from 'components/ProgressBar';
import ProjectBadge from 'components/projects/ProjectBadge';
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { RegularActivityTypes } from 'redux/modules/activities.types';
import { NotificationColors } from 'redux/modules/notifications.types';
import type { Activity } from 'types/activity';
import { getActivityColor } from 'utils/activity';
import styles from './ActivityMessage.module.scss';

interface UserMessageProps {
  children?: string;
  user: {
    id: number;
    name: string;
    avatar: string | null | undefined;
  };
}

const UserMessage: React.FC<UserMessageProps> = props => {
  const { user, children } = props;
  const userLink = `/users/${user.id}`;
  return (
    <span>
      <Avatar src={user.avatar} name={user.name} size="small" shape="circle" />
      <Link to={userLink} className="fs-mask">
        {user.name}
      </Link>
      {` ${children}`}
    </span>
  );
};

interface ActivityMessageProps {
  activity: Activity;
  useContextName?: boolean;
  className?: string;
}

class ActivityMessage extends Component<ActivityMessageProps> {
  renderProject = () => {
    const { project } = this.props.activity;

    return (
      <span>
        {' '}
        <ProjectBadge
          style={{ verticalAlign: 'text-bottom' }}
          size="small"
          color={getActivityColor(this.props.activity)}
          ticMeta={project.tic}
        />{' '}
        <DiagnosticResourceId resource="project" id={project.id!} />
        <Link to={`/projects/${project.id}`} className="fs-mask">
          <strong>{project.name}</strong>
        </Link>
      </span>
    );
  };

  renderUserAvatar = () => {
    const { user } = this.props.activity;
    return (
      <span>
        <Avatar src={user.avatar} name={user.name} size="small" shape="circle" />
      </span>
    );
  };

  render() {
    const { activity, useContextName } = this.props;
    const { user, project, client } = activity;

    if (activity.isAnomaly) {
      return (
        <span>
          <DynamicUserAvatar
            className={styles.dynamicAvatarWrap}
            size="small"
            hasNotification
            user={user}
            renderNotificationStyles={{
              color: NotificationColors.anomaly,
              icon: <AvatarAnomaly />,
            }}
          />
          <DiagnosticResourceId resource="user" id={user.id} />
          <Link to={`/users/${user.id}`} className="fs-mask">
            {user.name}
          </Link>{' '}
          {activity.message} {useContextName ? this.renderProject() : <span> this project</span>}
        </span>
      );
    }

    switch (activity.type) {
      case RegularActivityTypes.invite_multiple_project:
        return <UserMessage user={this.props.activity.user}>{activity.message}</UserMessage>;
      case RegularActivityTypes.logged_first_time: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.action,
                icon: <AvatarEdit color={NotificationColors.action} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            logged hours for the first time on
            {useContextName ? this.renderProject() : <span> this project</span>}
          </span>
        );
      }
      case RegularActivityTypes.project_budget_progress_fifty:
      case RegularActivityTypes.project_budget_progress_eighty:
      case RegularActivityTypes.project_budget_progress_hundred: {
        const numberMap = {
          project_budget_progress_fifty: 50,
          project_budget_progress_eighty: 80,
          project_budget_progress_hundred: 100,
        };
        return (
          <span>
            Budget reached
            <ProgressBar
              size="small"
              value={numberMap[activity.type]}
              className={styles.progressbar}
            />
            <strong>{numberMap[activity.type]}%</strong>
            {useContextName ? <span> on {this.renderProject()}</span> : null}
          </span>
        );
      }
      case RegularActivityTypes.create_report:
      case RegularActivityTypes.shared_report: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.action,
                icon: <AvatarCreate color={NotificationColors.action} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
            {useContextName ? (
              <span>
                <DiagnosticResourceId resource="project" id={project.id!} />
                <strong> {project.name}</strong>
              </span>
            ) : (
              <span> this project</span>
            )}
          </span>
        );
      }
      case RegularActivityTypes.delete_project: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.warning,
                icon: <AvatarDeleted color={NotificationColors.warning} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
            {useContextName ? this.renderProject() : <span> this project</span>}
          </span>
        );
      }
      case RegularActivityTypes.archive_client: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.archived,
                icon: <AvatarArchived color={NotificationColors.archived} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
          </span>
        );
      }
      case RegularActivityTypes.archive_project: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.archived,
                icon: <AvatarArchived color={NotificationColors.archived} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
            {useContextName ? this.renderProject() : <span> this project</span>}
          </span>
        );
      }
      case RegularActivityTypes.create_client: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.action,
                icon: <AvatarCreate color={NotificationColors.action} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
            {useContextName && (
              <>
                <DiagnosticResourceId resource="client" id={client.id!} />
                <strong> {client.name}</strong>
              </>
            )}
          </span>
        );
      }
      case RegularActivityTypes.create_project: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.action,
                icon: <AvatarCreate color={NotificationColors.action} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
            {useContextName ? this.renderProject() : <span> this project</span>}
          </span>
        );
      }
      case RegularActivityTypes.invite_project: {
        return (
          <span>
            <DynamicUserAvatar
              className={styles.dynamicAvatarWrap}
              size="small"
              hasNotification
              user={user}
              renderNotificationStyles={{
                color: NotificationColors.action,
                icon: <AvatarEdit color={NotificationColors.action} />,
              }}
            />
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
            {useContextName ? this.renderProject() : <span> this project</span>}
          </span>
        );
      }
      default:
        return (
          <span>
            {this.renderUserAvatar()}
            <DiagnosticResourceId resource="user" id={user.id} />
            <Link to={`/users/${user.id}`} className="fs-mask">
              {user.name}
            </Link>{' '}
            {activity.message}
            {useContextName ? this.renderProject() : <span> this project</span>}
          </span>
        );
    }
  }
}

export default ActivityMessage;
